// SearchTableOrders.js

import React, { useMemo, useState } from 'react';
import { FiSearch, FiFilter, FiClock } from 'react-icons/fi';
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table';
import { useNavigate } from 'react-router-dom';
import PaymentConfirmation from 'components/modal/PayementConfirmation';

function SearchTableOrders(props) {
  const { columnsData, tableData } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const navigate = useNavigate();

  // Custom global filter function to search across multiple columns
  const globalFilterFunction = useMemo(
    () => (rows, columnIds, filterValue) => {
      return rows.filter(row => {
        const searchLower = filterValue.toLowerCase();
        
        // Search across multiple columns
        const searchableColumns = ['name', 'email', 'phone', 'paymentMethod', 'status', 'price'];
        
        return searchableColumns.some(columnId => {
          const value = row.values[columnId];
          return value && value.toString().toLowerCase().includes(searchLower);
        });
      });
    },
    []
  );

  // Apply additional filters
  const filteredData = useMemo(() => {
    let filtered = data;

    // Filter by payment method
    if (filterPaymentMethod !== 'all') {
      filtered = filtered.filter(row => row.paymentMethod === filterPaymentMethod);
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(row => row.status === filterStatus);
    }

    return filtered;
  }, [data, filterPaymentMethod, filterStatus]);

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      globalFilter: globalFilterFunction,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
  } = tableInstance;

  const { pageIndex, pageSize } = state;

  const handleEditOrder = (orderId, userId) => {
    // Fix the navigation path - use correct route
    navigate(`/admin/main/ecommerce/order-details/${orderId}`, {
      state: { userId },
    });
  };

  const handleConfirmPayment = (subscriptionId, paymentInfo) => {
    setSelectedSubscriptionId(subscriptionId);
    setSelectedPaymentInfo(paymentInfo);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSubscriptionId(null);
    setSelectedPaymentInfo({});
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setGlobalFilter(value);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterPaymentMethod('all');
    setFilterStatus('all');
    setGlobalFilter('');
  };

  // Quick filter for latest M-Pesa payments
  const showLatestMpesa = () => {
    setFilterPaymentMethod('Mpesa');
    setFilterStatus('In Progress');
    setSearchTerm('');
    setGlobalFilter('');
  };

  return (
    <div className="w-full">
      {/* Enhanced Search and Filter Bar */}
      <div className="mb-4 space-y-3">
        {/* Search Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1">
            <FiSearch className="text-gray-400" />
            <input
              type="text"
              placeholder="Search across all columns (name, email, phone, payment method, status, price)..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:outline-none"
            />
          </div>
          <button
            onClick={clearFilters}
            className="ml-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Clear
          </button>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <FiFilter className="text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          {/* Payment Method Filter */}
          <select
            value={filterPaymentMethod}
            onChange={(e) => setFilterPaymentMethod(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All Payment Methods</option>
            <option value="Mpesa">M-Pesa Only</option>
            <option value="WaafiPay">WaafiPay Only</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded border border-gray-300 px-3 py-1 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="In Progress">In Progress Only</option>
            <option value="Completed">Completed Only</option>
            <option value="Pending">Pending Only</option>
            <option value="Failed">Failed Only</option>
          </select>

          {/* Quick Filter Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={showLatestMpesa}
              className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
            >
              <FiClock className="w-3 h-3" />
              <span>Latest M-Pesa</span>
            </button>
          </div>

          {/* Results Count */}
          <span className="text-sm text-gray-600">
            {filteredData.length} of {data.length} orders
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="w-full">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr key={row.id} {...row.getRowProps()} className="hover:bg-gray-50">
                  {row.cells.map((cell, index) => {
                    let data = cell.render('Cell');
                    
                    if (cell.column.Header === 'PAYMENT METHOD') {
                      const paymentMethod = cell.value;
                      data = (
                        <div className="flex items-center">
                          <div
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              paymentMethod === 'M-Pesa'
                                ? 'bg-green-100 text-green-800'
                                : paymentMethod === 'WaafiPay'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {paymentMethod}
                          </div>
                        </div>
                      );
                    } else if (cell.column.Header === 'STATUS ORDER') {
                      const status = cell.value;
                      data = (
                        <div className="flex items-center">
                          <div
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              status === 'Completed'
                                ? 'bg-green-100 text-green-800'
                                : status === 'In Progress'
                                ? 'bg-yellow-100 text-yellow-800'
                                : status === 'Pending'
                                ? 'bg-orange-100 text-orange-800'
                                : status === 'Failed'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {status}
                          </div>
                          {/* Show billing status if available */}
                          {row.original.billingStatus && row.original.billingStatus !== 'N/A' && (
                            <div className="ml-2 text-xs text-gray-500">
                              ({row.original.billingStatus})
                            </div>
                          )}
                        </div>
                      );
                    } else if (cell.column.Header === 'TOTAL PRICE') {
                      data = (
                        <div className="font-medium text-navy-700 dark:text-white">
                          {cell.value}
                        </div>
                      );
                    } else if (cell.column.Header === 'ORDER ACTIONS') {
                      const canConfirmMpesa = row.original.status === 'In Progress' && 
                                            row.original.hasBilling && 
                                            row.original.paymentMethod === 'Mpesa';
                      
                      const canConfirmWaafiPay = row.original.status === 'In Progress' && 
                                                row.original.hasBilling && 
                                                row.original.paymentMethod === 'WaafiPay';
                      
                      data = (
                        <div className="flex space-x-2">
                          <span
                            className="text-sm font-medium text-brand-500 underline hover:cursor-pointer dark:text-white"
                            onClick={() => handleEditOrder(row.original.id, row.original.userId)}
                          >
                            View User
                          </span>
                          {canConfirmMpesa ? (
                            <span
                              className="text-sm font-medium text-green-500 underline hover:cursor-pointer dark:text-white"
                              onClick={() => handleConfirmPayment(row.original.id, {
                                paymentMethod: row.original.paymentMethod,
                                accountReference: row.original.accountReference,
                                transactionId: row.original.transactionId,
                                amount: row.original.price,
                                userPhone: row.original.phone
                              })}
                            >
                              Confirm M-Pesa
                            </span>
                          ) : canConfirmWaafiPay ? (
                            <span className="text-sm font-medium text-blue-500 cursor-not-allowed">
                              WaafiPay Auto
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-gray-400 cursor-not-allowed">
                              {!row.original.hasBilling ? 'No Billing Info' : 
                               row.original.status !== 'In Progress' ? 'Payment Already Processed' : 
                               'Cannot Confirm'}
                            </span>
                          )}
                        </div>
                      );
                    }
                    return (
                      <td
                        key={cell.id}
                        className="px-4 py-4"
                        {...cell.getCellProps()}
                      >
                        {data}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-2 flex h-20 w-full items-center justify-between px-6">
        {/* Entries Range */}
        <div className="text-sm text-gray-700">
          Showing {pageSize * pageIndex + 1} to{' '}
          {Math.min(pageSize * (pageIndex + 1), filteredData.length)} of {filteredData.length} entries
        </div>

        {/* Current Page and Buttons */}
        <div className="flex items-center space-x-4">
          {/* Current Page Indicator */}
          <div className="text-sm text-gray-600">
            Page {pageIndex + 1} of {Math.ceil(filteredData.length / pageSize)}
          </div>

          {/* Navigation Buttons */}
          <div className="flex space-x-2">
            <button
              onClick={() => tableInstance.previousPage()}
              disabled={!tableInstance.canPreviousPage}
              className={`px-3 py-1 rounded-md ${
                !tableInstance.canPreviousPage
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-900 text-white hover:bg-orange-900'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => tableInstance.nextPage()}
              disabled={!tableInstance.canNextPage}
              className={`px-3 py-1 rounded-md ${
                !tableInstance.canNextPage
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-brand-900 text-white hover:bg-orange-900'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Payment Confirmation Modal */}
      <PaymentConfirmation
        isOpen={isModalOpen}
        onClose={closeModal}
        subscriptionId={selectedSubscriptionId}
        paymentInfo={selectedPaymentInfo}
      />
    </div>
  );
}

export default SearchTableOrders;
